import React from 'react';
import {
  Box,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Compare as CompareIcon,
  CreateNewFolder as CreateNewFolderIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFileExplorerContext } from '../../FileExplorerContext';
import FileSourceButtons from '../../../FileSourceButtons';

/**
 * ActionToolbar - Toolbar with view mode toggle and action buttons
 *
 * Features:
 * - View mode toggle (table/grid)
 * - Compare mode toggle
 * - Create folder button
 * - Choose from Filer and upload buttons (FileSourceButtons)
 * - Custom action buttons
 *
 * Actions are labelled buttons rather than bare icons, so they can be found
 * without hovering.
 */
export const ActionToolbar = ({
  enableViewToggle = true,
  enableCompareMode = false,
  enableCreateFolder = false,
  enableUploadFile = false,
  onCreateFolder,
  onUploadFile,
  onChooseFile,
  actions,
}) => {
  const { t } = useTranslation(['files', 'common']);
  const { viewMode, setViewMode, compareMode, setCompareMode } = useFileExplorerContext();

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Left side - View mode toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {enableViewToggle && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label={t('files:view_mode.label')}
          >
            <Tooltip title={t('files:view_mode.table')}>
              <ToggleButton value="table" aria-label={t('files:view_mode.table')}>
                <ViewListIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t('files:view_mode.grid')}>
              <ToggleButton value="grid" aria-label={t('files:view_mode.grid')}>
                <ViewModuleIcon />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Right side - Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {enableCompareMode && !compareMode && (
          <Button size="small" startIcon={<CompareIcon />} onClick={() => setCompareMode(true)}>
            {t('files:actions.compare')}
          </Button>
        )}

        {enableCreateFolder && (
          <Button size="small" variant="outlined" startIcon={<CreateNewFolderIcon />} onClick={onCreateFolder}>
            {t('files:create_folder')}
          </Button>
        )}

        {actions}

        <FileSourceButtons onChoose={onChooseFile} onUpload={enableUploadFile ? onUploadFile : undefined} />
      </Box>
    </Box>
  );
};
